import React, { useEffect, useState } from 'react';
import { carteraApi } from '../api';
import { C, MONO, chip } from './estilos';

/**
 * Plantillas de WhatsApp del bot con lo que Meta tiene dado de alta
 * (GET /bot-ventas/plantillas). Solo lectura.
 *
 * A la izquierda la lista; al elegir una, la derecha muestra cómo se ve el
 * mensaje: encabezado, cuerpo con sus {{n}} como chip del parámetro, pie y
 * botones.
 */

export const usePlantillasBot = () => {
  const [estado, setEstado] = useState({ cargando: true, plantillas: [], error: '' });

  useEffect(() => {
    const control = new AbortController();
    carteraApi.getBotVentasPlantillas(control.signal)
      .then(({ data }) => {
        setEstado({
          cargando: false,
          plantillas: Array.isArray(data?.plantillas) ? data.plantillas : [],
          error: data?.error || '',
        });
      })
      .catch((e) => {
        if (control.signal.aborted) return;
        const respuesta = e?.response;
        const error = respuesta?.data?.message
          || (respuesta ? 'No se pudieron leer las plantillas de Meta.' : 'No hay conexión con el servidor. Revisa tu red y vuelve a intentar.');
        setEstado({ cargando: false, plantillas: [], error });
      });
    return () => control.abort();
  }, []);

  return estado;
};

const ESTADOS = {
  APPROVED: { texto: 'Aprobada', color: C.verde, borde: C.verde },
  PENDING: { texto: 'Pendiente', color: C.naranjaTexto, borde: C.naranja },
  IN_APPEAL: { texto: 'Pendiente', color: C.naranjaTexto, borde: C.naranja },
  PAUSED: { texto: 'Pausada', color: C.naranjaTexto, borde: C.naranja },
  REJECTED: { texto: 'Rechazada', color: '#B42318', borde: '#F2CACA' },
  DISABLED: { texto: 'Desactivada', color: '#B42318', borde: '#F2CACA' },
};

const CATEGORIAS = { UTILITY: 'Utilidad', MARKETING: 'Marketing', AUTHENTICATION: 'Autenticación' };

export const estadoPlantilla = (p) => {
  if (!p.encontrada) return { texto: 'No está en Meta', color: C.gris, borde: C.apagado };
  return ESTADOS[p.estado] || { texto: p.estado || 'Sin estado', color: C.gris, borde: C.apagado };
};

export const ChipEstado = ({ plantilla }) => {
  const e = estadoPlantilla(plantilla);
  return <span style={chip(e.color, e.borde)}>{e.texto}</span>;
};

const paraTexto = (para) => (para === 'lead' ? 'Le llega al lead' : 'Le llega al asesor');

export const FilaPlantilla = ({ plantilla, elegida, onElegir }) => (
  <button
    type="button"
    onClick={() => onElegir(plantilla.clave)}
    aria-pressed={elegida}
    style={{
      display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', gap: '4px 12px', alignItems: 'center',
      textAlign: 'left', width: '100%', padding: 12, borderRadius: 14, cursor: 'pointer', fontFamily: 'inherit',
      border: `1.5px solid ${elegida ? C.cyanBorde : 'transparent'}`,
      background: elegida ? C.cyanTinte : 'transparent',
    }}
  >
    <span style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
      <span style={{ fontSize: 14, fontWeight: 600, color: C.ink }}>{plantilla.titulo}</span>
      <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'baseline', fontSize: 12, color: C.gris }}>
        <span>
          <i className={`pi ${plantilla.para === 'lead' ? 'pi-user' : 'pi-briefcase'}`} style={{ fontSize: 10, marginRight: 4 }} />
          {paraTexto(plantilla.para)}
        </span>
        {plantilla.encontrada && plantilla.categoria && <span>· {CATEGORIAS[plantilla.categoria] || plantilla.categoria}</span>}
      </span>
      <code style={{ fontFamily: MONO, fontSize: 11.5, color: C.verde, overflowWrap: 'anywhere' }}>{plantilla.nombre}</code>
    </span>
    <ChipEstado plantilla={plantilla} />
  </button>
);

export const SeccionPlantillas = ({ cargando, plantillas, error, elegida, onElegir }) => (
  <section
    aria-label="Plantillas de WhatsApp"
    style={{ background: '#fff', borderRadius: 16, padding: '16px 16px 8px', display: 'flex', flexDirection: 'column', border: `1px solid ${C.lineaSuave}` }}
  >
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '0 4px 10px', borderBottom: `1px solid ${C.lineaSuave}`, marginBottom: 6 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: C.steel }}>Plantillas de WhatsApp</h3>
        <span style={{ fontSize: 12.5, color: C.gris, display: 'flex', gap: 6, alignItems: 'center' }}>
          <i className="pi pi-lock" style={{ fontSize: 11 }} />Solo lectura · vienen de Meta
        </span>
      </div>
      <p style={{ margin: 0, fontSize: 13.5, color: C.ink3, lineHeight: 1.45 }}>
        Los mensajes que el bot manda fuera de la conversación. Elige una para ver cómo se ve.
      </p>
    </div>
    {error && (
      <div role="alert" style={{ margin: '4px 4px 8px', padding: '9px 12px', borderRadius: 10, background: '#FBE7E7', border: '1px solid #F2CACA', color: '#B42318', fontSize: 12.5, lineHeight: 1.4 }}>
        <i className="pi pi-exclamation-triangle" style={{ fontSize: 11, marginRight: 6 }} />{error}
      </div>
    )}
    {cargando && (
      <div style={{ padding: '10px 4px', fontSize: 12.5, color: C.gris }}>
        <span className="pi pi-spin pi-spinner" style={{ fontSize: 11, marginRight: 6 }} />Cargando plantillas…
      </div>
    )}
    {!cargando && plantillas.length === 0 && !error && (
      <div style={{ padding: '10px 4px', fontSize: 12.5, color: C.gris }}>No hay plantillas para mostrar.</div>
    )}
    {plantillas.map((p) => (
      <FilaPlantilla key={p.clave} plantilla={p} elegida={elegida === p.clave} onElegir={onElegir} />
    ))}
  </section>
);

/** Pinta un texto de plantilla cambiando cada {{n}} por un chip «parámetro». */
export const TextoConParametros = ({ texto, parametros }) => {
  const partes = String(texto || '').split(/(\{\{\s*\d+\s*\}\})/);
  return partes.map((parte, i) => {
    const m = parte.match(/^\{\{\s*(\d+)\s*\}\}$/);
    if (!m) return <React.Fragment key={i}>{parte}</React.Fragment>;
    const n = Number(m[1]);
    const nombre = (parametros || [])[n - 1] || String(n);
    return (
      <span
        key={i}
        data-parametro={n}
        style={{ display: 'inline-block', padding: '0 6px', margin: '0 1px', borderRadius: 6, background: '#EAF7FC', border: '1px solid #CBE7F4', color: C.steel, fontSize: 12.5, fontWeight: 600, fontStyle: 'normal', lineHeight: '18px' }}
      >
        «{nombre}»
      </span>
    );
  });
};

export const PanelPlantilla = ({ plantilla }) => {
  if (!plantilla) return null;
  const { encontrada, parametros } = plantilla;
  return (
    <aside
      aria-label="Cómo se ve la plantilla"
      style={{ background: '#fff', borderRadius: 16, display: 'flex', flexDirection: 'column', border: `1px solid ${C.lineaSuave}` }}
    >
      <div style={{ padding: '14px 18px 12px', display: 'flex', flexDirection: 'column', gap: 7, borderBottom: `1px solid ${C.lineaSuave}` }}>
        <span style={{ fontSize: 11.5, letterSpacing: '0.06em', textTransform: 'uppercase', color: C.verde, fontWeight: 600 }}>
          Cómo se ve la plantilla
        </span>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 600, color: C.steel }}>{plantilla.titulo}</h3>
          <ChipEstado plantilla={plantilla} />
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', fontSize: 12, color: C.gris }}>
          <span>{paraTexto(plantilla.para)}</span>
          <code style={{ fontFamily: MONO, color: C.verde }}>{plantilla.nombre}</code>
          {encontrada && plantilla.categoria && <span>{CATEGORIAS[plantilla.categoria] || plantilla.categoria}</span>}
          {encontrada && plantilla.idioma && <span>{plantilla.idioma}</span>}
        </div>
      </div>
      <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {plantilla.cuando && (
          <div style={{ fontSize: 13, color: C.ink2, lineHeight: 1.45 }}>
            <b style={{ color: C.ink }}>Cuándo se manda:</b> {plantilla.cuando}
          </div>
        )}
        {encontrada ? (
          <div style={{ background: '#EFEAE2', borderRadius: 14, padding: 14 }}>
            <div
              data-testid="burbuja-plantilla"
              style={{ maxWidth: 360, background: '#fff', borderRadius: '4px 12px 12px 12px', boxShadow: '0 1px 1px rgba(0,0,0,.12)', overflow: 'hidden' }}
            >
              <div style={{ padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13.5, lineHeight: 1.45, color: '#111B21', whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}>
                {plantilla.encabezado && (
                  <div style={{ fontWeight: 700 }}><TextoConParametros texto={plantilla.encabezado} parametros={parametros} /></div>
                )}
                <div><TextoConParametros texto={plantilla.cuerpo} parametros={parametros} /></div>
                {plantilla.pie && <div style={{ fontSize: 12, color: '#667781' }}>{plantilla.pie}</div>}
              </div>
              {Array.isArray(plantilla.botones) && plantilla.botones.map((b) => (
                <div key={b} style={{ borderTop: '1px solid #E9EDEF', padding: '8px 10px', textAlign: 'center', color: '#00A5F4', fontSize: 13.5, fontWeight: 500 }}>
                  {b}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div role="note" style={{ padding: '10px 12px', borderRadius: 10, border: `1px dashed ${C.apagado}`, color: C.ink2, fontSize: 13, lineHeight: 1.45 }}>
            Esta plantilla no está dada de alta en Meta con ese nombre, así que no se puede mandar.
          </div>
        )}
        {Array.isArray(parametros) && parametros.length > 0 && (
          <div style={{ fontSize: 12, color: C.gris, lineHeight: 1.5 }}>
            Datos que se llenan al mandarla: {parametros.map((p, i) => `{{${i + 1}}} ${p}`).join(' · ')}
          </div>
        )}
      </div>
    </aside>
  );
};
